import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CARNIVAL_STATIONS } from '../game/gameConfig';

const TOTAL_STAMPS = 20;
const ROUND_DURATION = 20000;

function CarnivalStage({ onComplete }) {
  const [round, setRound] = useState(0);
  const [active, setActive] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [feedback, setFeedback] = useState('尋找亮起的詠春體驗站');
  const [remaining, setRemaining] = useState(ROUND_DURATION);
  const stats = useRef({ hits: 0, bestCombo: 0, score: 0 });
  const completed = useRef(false);
  const startedAt = useRef(Date.now());
  const order = useMemo(() => (
    Array.from({ length: TOTAL_STAMPS }, () => (
      Math.floor(Math.random() * CARNIVAL_STATIONS.length)
    ))
  ), []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const nextRemaining = Math.max(
        0,
        ROUND_DURATION - (Date.now() - startedAt.current),
      );
      setRemaining(nextRemaining);

      if (nextRemaining === 0 && !completed.current) {
        completed.current = true;
        window.clearInterval(timer);
        onComplete(stats.current);
      }
    }, 50);

    return () => window.clearInterval(timer);
  }, [onComplete]);

  useEffect(() => {
    if (completed.current) {
      return undefined;
    }

    setActive(order[round % TOTAL_STAMPS] ?? 0);
    const timeout = window.setTimeout(() => {
      setCombo(0);
      setFeedback('錯過體驗，下個展位見');
      setRound((value) => value + 1);
    }, 1900);

    return () => window.clearTimeout(timeout);
  }, [onComplete, order, round]);

  const collectStamp = (index) => {
    if (index !== active) {
      setFeedback('請點按正在亮起的體驗站');
      return;
    }

    const nextCombo = combo + 1;
    const gain = 170 + nextCombo * 12;
    stats.current = {
      hits: stats.current.hits + 1,
      bestCombo: Math.max(stats.current.bestCombo, nextCombo),
      score: stats.current.score + gain,
    };
    setScore(stats.current.score);
    setCombo(nextCombo);
    setFeedback(`已體驗：${CARNIVAL_STATIONS[index].name}`);
    navigator.vibrate?.(20);
    setRound((value) => value + 1);
  };

  return (
    <main className="screen-panel themed-stage">
      <header className="compact-game-header">
        <div>
          <span className="eyebrow">第二關 · 詠春嘉年華</span>
          <h2>文化體驗巡遊</h2>
          <p>亮起的展位正在開放，直接點按收集文化體驗印章。</p>
        </div>
        <div className="score-block">
          <small>活動時間 17:00 — 20:00</small>
          <small>挑戰倒數 {(remaining / 1000).toFixed(1)} 秒</small>
          <strong>{score.toLocaleString()}</strong>
        </div>
      </header>

      <div className="stage-progress">
        <span style={{ width: `${(remaining / ROUND_DURATION) * 100}%` }} />
      </div>

      <section className="carnival-arena">
        <div className="carnival-sign">
          10 月 3 日
          <span>詠春千人匯演 · 詠春嘉年華</span>
        </div>

        <div className="station-grid">
          {CARNIVAL_STATIONS.map((station, index) => {
            const isActive = index === active && !completed.current;
            return (
              <motion.button
                className={`station-card ${isActive ? 'is-active' : ''}`}
                key={station.name}
                type="button"
                onClick={() => collectStamp(index)}
                whileTap={{ scale: 0.94 }}
                animate={isActive ? { y: [0, -5, 0] } : { y: 0 }}
                transition={isActive ? { repeat: Infinity, duration: 0.9 } : undefined}
              >
                <span className="station-symbol">{station.symbol}</span>
                <strong>{station.name}</strong>
                <small>{station.detail}</small>
                {isActive && <i>體驗中</i>}
              </motion.button>
            );
          })}
        </div>

        <div className="stage-feedback">{feedback}</div>
        <div className="stage-combo">印章 <strong>{round}</strong></div>
      </section>
    </main>
  );
}

export default CarnivalStage;